(function () {
  'use strict';

  if (window.fwdWishlistInitialized) return;
  window.fwdWishlistInitialized = true;

  const BUTTON_SELECTOR = '.fwd-wishlist-button[data-variant-id], .fwd-product-wishlist-button[data-variant-id]';
  const HEADER_COUNT_SELECTOR = '.fwd-header-wishlist-count';
  const PAGE_SELECTOR = '[data-fwd-wishlist-page]';
  const STORAGE_KEY = 'fwd:wishlist:v1';
  const PENDING_STORAGE_KEY = 'fwd:wishlist:pending:v1';
  const RECONCILIATION_STORAGE_KEY = 'fwd:wishlist:reconciled:v1';
  const SWYM_READY_TIMEOUT_MS = 8000;
  const SWYM_OPERATION_TIMEOUT_MS = 6000;
  const RETRY_DELAYS_MS = [1500, 5000, 15000, 60000];
  let swymApi = null;
  let wishlistItems = [];
  let pendingOperations = [];
  let toastTimer = null;
  let readyTimer = null;
  let retryTimer = null;
  let retryAttempt = 0;
  let syncInFlight = false;
  let eventsBound = false;
  let swymEventsBound = false;
  let initialized = false;
  let cartVariantIds = new Set();

  function track(eventName, properties) {
    try {
      window.fwdAnalytics?.track?.(eventName, properties);
    } catch (_error) {
      // Analytics must never affect wishlist interactions.
    }
  }

  function variantId(value) {
    return String(value == null ? '' : value).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
  }

  function productId(value) {
    return String(value == null ? '' : value).replace(/^gid:\/\/shopify\/Product\//, '');
  }

  function itemVariantId(item) {
    return variantId(item && (item.epi || item.variantId || item.variant_id));
  }

  function normalizeWishlistItems(items) {
    const unique = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
      const id = itemVariantId(item);
      if (id) unique.set(id, item);
    });
    return Array.from(unique.values());
  }

  function readLocalWishlist() {
    try {
      const stored = window.localStorage && window.localStorage.getItem(STORAGE_KEY);
      if (stored == null) return null;
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? normalizeWishlistItems(parsed) : null;
    } catch (error) {
      return null;
    }
  }

  function persistWishlist() {
    try {
      if (window.localStorage) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch (error) {
      // Keep the in-memory wishlist usable when storage is unavailable.
    }
  }

  function readPendingOperations() {
    try {
      const stored = window.localStorage && window.localStorage.getItem(PENDING_STORAGE_KEY);
      if (stored == null) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      const latest = new Map();
      parsed.forEach((operation) => {
        const id = itemVariantId(operation && operation.item);
        if (!id || !['addToWishList', 'removeFromWishList'].includes(operation.method)) return;
        latest.set(id, operation);
      });
      return Array.from(latest.values());
    } catch (error) {
      return [];
    }
  }

  function persistPendingOperations() {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(pendingOperations));
      }
    } catch (error) {
      // The current page remains usable even when durable retry storage is unavailable.
    }
  }

  function needsLegacyReconciliation() {
    try {
      return !window.localStorage || window.localStorage.getItem(RECONCILIATION_STORAGE_KEY) !== 'complete';
    } catch (error) {
      return false;
    }
  }

  function markLegacyReconciled() {
    try {
      if (window.localStorage) window.localStorage.setItem(RECONCILIATION_STORAGE_KEY, 'complete');
    } catch (error) {
      // A later successful page load can safely repeat the idempotent union.
    }
  }

  function findWishlistItem(id) {
    const normalized = variantId(id);
    return wishlistItems.find((item) => itemVariantId(item) === normalized);
  }

  function buttonData(button) {
    return {
      epi: variantId(button.dataset.variantId),
      empi: productId(button.dataset.productId),
      du: button.dataset.productUrl,
      product_category: button.dataset.productCategory,
      product_name: button.dataset.productName,
      product_url: button.dataset.productUrl,
      brand: button.dataset.brand,
      size: button.dataset.size,
      variant_title: button.dataset.variantTitle,
      price: button.dataset.price,
      currency: button.dataset.currency,
      list_name: button.dataset.listName,
      list_position: button.dataset.listPosition,
      placement: button.dataset.placement,
    };
  }

  function itemProperties(item, placement) {
    return {
      product_id: item && (item.empi || item.productId || item.product_id),
      variant_id: itemVariantId(item),
      product_category: item && item.product_category,
      product_name: item && item.product_name,
      product_url: item && (item.product_url || item.du || item.url),
      brand: item && item.brand,
      size: item && item.size,
      variant_title: item && item.variant_title,
      price: item && item.price,
      currency: item && item.currency,
      list_name: item && item.list_name,
      list_position: item && item.list_position,
      placement: placement || (item && item.placement),
    };
  }

  function cartRequestId() {
    try {
      if (window.crypto?.randomUUID) return `cart-${window.crypto.randomUUID()}`;
    } catch (_error) {
      // Fall through to a request-local non-secret identifier.
    }
    return `cart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function cartResponseItems(cartData) {
    if (Array.isArray(cartData?.items)) return cartData.items;
    return cartData && (cartData.variant_id || cartData.id) ? [cartData] : [];
  }

  function responseSelectedSize(item) {
    const options = Array.isArray(item?.options_with_values) ? item.options_with_values : [];
    return options.find((option) => ['size', '사이즈'].includes(String(option?.name || '').trim().toLowerCase()))?.value;
  }

  function trackAddedCartItems(cartData, requestedItems, requestId) {
    const requested = new Map(requestedItems.map((item) => [variantId(item.variant_id), item]));
    const tracked = new Set();
    cartResponseItems(cartData).forEach((responseItem) => {
      const responseVariantId = variantId(responseItem?.variant_id || responseItem?.id);
      const requestedItem = requested.get(responseVariantId);
      if (!requestedItem || tracked.has(responseVariantId)) return;
      tracked.add(responseVariantId);
      track('Cart Item Added', Object.assign(itemProperties(requestedItem, 'wishlist'), {
        cart_request_id: requestId,
        product_id: responseItem?.product_id || requestedItem.product_id,
        variant_id: responseVariantId,
        product_name: responseItem?.product_title || requestedItem.product_name,
        variant_title: responseItem?.variant_title || requestedItem.variant_title,
        brand: requestedItem.brand || responseItem?.vendor,
        size: responseSelectedSize(responseItem) || requestedItem.size,
        price: responseItem?.final_price ?? requestedItem.price,
        quantity: requestedItem.quantity,
        cart_quantity: responseItem?.quantity,
      }));
    });
    return tracked.size;
  }

  function setButtonState(button, isAdded) {
    const isProductButton = button.classList.contains('fwd-product-wishlist-button');
    const eligible = button.dataset.wishlistEligible !== 'false';
    const disabled = !eligible && !isAdded;
    const label = isProductButton
      ? isAdded ? '찜 해제' : '찜하기'
      : isAdded ? '위시리스트에서 제거' : '위시리스트에 추가';
    button.classList.toggle('is-added', isAdded);
    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
    button.setAttribute('aria-pressed', String(isAdded));
    button.setAttribute('aria-label', label);
    const text = button.querySelector('.fwd-product-wishlist-button__label');
    if (text) text.textContent = label;
  }

  function syncButtons() {
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      setButtonState(button, Boolean(findWishlistItem(button.dataset.variantId)));
    });
  }

  function updateHeaderCount() {
    const count = wishlistItems.length;
    document.querySelectorAll(HEADER_COUNT_SELECTOR).forEach((element) => {
      const displayCount = count > 99 ? '99+' : String(count);
      const value = element.querySelector('.fwd-header-wishlist-count__value');
      if (value) value.textContent = displayCount;
      element.hidden = count === 0;
      element.setAttribute('aria-label', `위시리스트 상품 ${count}개`);
      element.closest('a')?.setAttribute('aria-label', count === 0 ? '위시리스트 열기' : `위시리스트 열기 (${count})`);
    });
  }

  function showToast(message) {
    let toast = document.querySelector('.fwd-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fwd-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => { toast.hidden = true; }, 2200);
  }

  function fetchWishlist(callback) {
    if (!swymApi || typeof swymApi.fetch !== 'function') {
      if (callback) callback(new Error('Wishlist API unavailable'));
      return;
    }
    try {
      swymApi.fetch(
        (items) => {
          if (callback) callback(null, normalizeWishlistItems(items));
        },
        (error) => { if (callback) callback(error || new Error('Wishlist fetch failed')); }
      );
    } catch (error) {
      if (callback) callback(error);
    }
  }

  function callWishlist(method, item) {
    return new Promise((resolve, reject) => {
      if (!swymApi || typeof swymApi[method] !== 'function') {
        reject(new Error(`Wishlist API ${method} unavailable`));
        return;
      }
      let settled = false;
      const settle = (callback) => (value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        callback(value);
      };
      let timeout = window.setTimeout(
        settle(reject),
        SWYM_OPERATION_TIMEOUT_MS
      );
      try {
        swymApi[method](
          item,
          settle(resolve),
          settle((error) => reject(error || new Error(`Wishlist API ${method} failed`)))
        );
      } catch (error) {
        settle(reject)(error);
      }
    });
  }

  function operationMatches(left, right) {
    return left
      && right
      && left.method === right.method
      && left.queuedAt === right.queuedAt
      && itemVariantId(left.item) === itemVariantId(right.item);
  }

  function queueWishlistOperation(method, item) {
    const id = itemVariantId(item);
    if (!id) return;
    pendingOperations = pendingOperations.filter((operation) => itemVariantId(operation.item) !== id);
    pendingOperations.push({ method, item: Object.assign({}, item), queuedAt: Date.now() });
    persistPendingOperations();
  }

  function acknowledgeOperation(operation) {
    pendingOperations = pendingOperations.filter((current) => !operationMatches(current, operation));
    persistPendingOperations();
  }

  function schedulePendingRetry() {
    window.clearTimeout(retryTimer);
    if (!pendingOperations.length || !swymApi) return;
    const delay = RETRY_DELAYS_MS[Math.min(retryAttempt, RETRY_DELAYS_MS.length - 1)];
    retryAttempt += 1;
    retryTimer = window.setTimeout(flushPendingOperations, delay);
  }

  async function flushPendingOperations() {
    if (syncInFlight || !swymApi || !pendingOperations.length) return;
    syncInFlight = true;
    const operations = pendingOperations.slice();
    const results = await Promise.allSettled(
      operations.map((operation) => callWishlist(operation.method, operation.item))
    );
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') acknowledgeOperation(operations[index]);
      // Rejected operations remain in durable storage and retry without blocking the UI.
    });
    syncInFlight = false;
    if (pendingOperations.length) {
      schedulePendingRetry();
    } else {
      retryAttempt = 0;
      window.clearTimeout(retryTimer);
    }
  }

  function reconcileWishlist(remoteItems) {
    const remoteById = new Map();
    normalizeWishlistItems(remoteItems).forEach((item) => remoteById.set(itemVariantId(item), item));

    if (needsLegacyReconciliation()) {
      wishlistItems.forEach((item) => {
        const id = itemVariantId(item);
        const hasPendingIntent = pendingOperations.some((operation) => itemVariantId(operation.item) === id);
        if (id && !remoteById.has(id) && !hasPendingIntent) queueWishlistOperation('addToWishList', item);
      });
      markLegacyReconciled();
    }

    pendingOperations.slice().forEach((operation) => {
      const id = itemVariantId(operation.item);
      if (operation.method === 'addToWishList') {
        if (remoteById.has(id)) acknowledgeOperation(operation);
        else remoteById.set(id, operation.item);
      } else {
        if (!remoteById.has(id)) acknowledgeOperation(operation);
        remoteById.delete(id);
      }
    });

    wishlistItems = Array.from(remoteById.values());
    persistWishlist();
    updateHeaderCount();
    syncButtons();
    renderWishlistPage();
    flushPendingOperations();
  }

  function refreshWishlistFromRemote() {
    fetchWishlist((error, remoteItems) => {
      if (!error) reconcileWishlist(remoteItems);
      else {
        renderWishlistPage();
        flushPendingOperations();
      }
    });
  }

  function toggleWishlist(button) {
    const data = buttonData(button);
    const existing = findWishlistItem(data.epi);
    if (!existing && (button.disabled || button.dataset.wishlistEligible === 'false')) return;
    const method = existing ? 'removeFromWishList' : 'addToWishList';
    const payload = existing ? Object.assign({}, data, existing) : data;

    if (existing) {
      wishlistItems = wishlistItems.filter((item) => itemVariantId(item) !== data.epi);
      showToast('위시리스트에서 삭제했습니다.');
    } else {
      wishlistItems.push(Object.assign({}, data));
      showToast('위시리스트에 추가했습니다.');
    }
    persistWishlist();
    queueWishlistOperation(method, payload);
    updateHeaderCount();
    syncButtons();
    renderWishlistPage();
    flushPendingOperations();
    track(existing ? 'Wishlist Item Removed' : 'Wishlist Item Added', Object.assign(
      itemProperties(payload),
      { wishlist_count: wishlistItems.length }
    ));
  }

  function wishlistProductPath(item) {
    const rawUrl = item && (item.du || item.url || item.product_url);
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl, window.location.origin);
      return url.pathname.startsWith('/products/') ? url.pathname : '';
    } catch (error) {
      return '';
    }
  }

  async function fetchProductCard(item) {
    const path = wishlistProductPath(item);
    if (!path) throw new Error('Wishlist product URL unavailable');
    const url = new URL(path, window.location.origin);
    const selectedVariantId = itemVariantId(item);
    if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);
    url.searchParams.set('view', 'wishlist-card');
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Wishlist card failed: ${response.status}`);
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const card = page.querySelector('[data-fwd-wishlist-card]');
    if (!card) throw new Error('Wishlist card markup unavailable');
    card.querySelectorAll('a[href]').forEach((link) => {
      const destination = new URL(link.getAttribute('href'), window.location.origin);
      if (destination.pathname.startsWith('/products/')) link.setAttribute('href', destination.pathname);
    });
    return card.innerHTML;
  }

  async function fetchCartVariantIds() {
    if (!window.routes || !window.routes.cart_url) return null;
    try {
      const response = await fetch(`${window.routes.cart_url.replace(/\.js$/, '')}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const cart = await response.json();
      return new Set((cart.items || []).map((item) => variantId(item.variant_id || item.id)));
    } catch (error) {
      return null;
    }
  }

  function syncWishlistCartButtons(root) {
    const page = root || document.querySelector(PAGE_SELECTOR);
    if (!page) return;
    const buttons = Array.from(page.querySelectorAll('[data-fwd-wishlist-add-to-cart]'));
    buttons.forEach((button) => {
      const available = button.dataset.available !== 'false';
      const inCart = cartVariantIds.has(variantId(button.dataset.variantId));
      const label = button.querySelector('span');
      button.dataset.cartAdded = String(inCart);
      button.disabled = !available || inCart || button.classList.contains('loading');
      if (label) {
        label.textContent = inCart
          ? '이미 카트에 추가됨'
          : (available ? '카트에 추가' : 'FOUND A NEW HOME · 품절');
      }
    });

    const addAllButton = page.querySelector('[data-fwd-wishlist-add-all]');
    if (addAllButton && !addAllButton.classList.contains('loading')) {
      addAllButton.disabled = !buttons.some((button) => !button.disabled);
    }
  }

  async function refreshWishlistCartState(root) {
    const currentVariantIds = await fetchCartVariantIds();
    if (currentVariantIds) cartVariantIds = currentVariantIds;
    syncWishlistCartButtons(root);
  }

  async function renderWishlistPage() {
    const page = document.querySelector(PAGE_SELECTOR);
    if (!page) return;
    const container = page.querySelector('#fwd-wishlist-render-container');
    const clearButton = page.querySelector('[data-fwd-wishlist-clear]');
    const addAllButton = page.querySelector('[data-fwd-wishlist-add-all]');
    if (!container) return;
    container.setAttribute('aria-busy', 'false');
    if (clearButton) clearButton.hidden = wishlistItems.length === 0;
    if (addAllButton) {
      addAllButton.hidden = wishlistItems.length === 0;
      addAllButton.disabled = wishlistItems.length > 0;
    }

    if (wishlistItems.length === 0) {
      renderEmptyWishlist(container);
      return;
    }

    container.innerHTML = '<ul class="grid product-grid grid--2-col-tablet-down grid--3-col-desktop" data-fwd-wishlist-grid></ul>';
    const grid = container.querySelector('[data-fwd-wishlist-grid]');
    const cards = await Promise.allSettled(wishlistItems.map(fetchProductCard));
    cards.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      const item = document.createElement('li');
      item.className = 'grid__item';
      item.innerHTML = result.value;
      grid.appendChild(item);
    });

    if (!grid.children.length) {
      container.innerHTML = '<p class="fwd-wishlist-page__error">위시리스트 상품을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>';
      return;
    }
    await refreshWishlistCartState(page);
    syncButtons();
  }

  function renderEmptyWishlist(container) {
    if (!container) return;
    container.setAttribute('aria-busy', 'false');
    container.innerHTML = '<div class="fwd-wishlist-empty"><p>위시리스트가 비어 있습니다.</p><a class="button button--secondary" href="/collections/all">상품 계속 보기</a></div>';
    const clearButton = document.querySelector('[data-fwd-wishlist-clear]');
    if (clearButton) clearButton.hidden = true;
    const addAllButton = document.querySelector('[data-fwd-wishlist-add-all]');
    if (addAllButton) addAllButton.hidden = true;
  }

  function setCartButtonLoading(button, loading) {
    if (!button) return;
    button.disabled = loading || button.dataset.cartAdded === 'true' || button.dataset.available === 'false';
    button.classList.toggle('loading', loading);
    button.setAttribute('aria-busy', String(loading));
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  async function addVariantsToCart(ids, button, skipExisting) {
    const mode = skipExisting ? 'all' : 'single';
    const requestId = cartRequestId();
    const initialVariantIds = Array.from(new Set(ids.map(variantId).filter(Boolean)));
    let variantIds = initialVariantIds.slice();
    const primaryItem = findWishlistItem(variantIds[0]);
    const baseProperties = Object.assign(mode === 'single' ? itemProperties(primaryItem || buttonData(button), 'wishlist') : { placement: 'wishlist' }, {
      cart_request_id: requestId,
      mode,
      candidate_count: initialVariantIds.length,
      requested_count: initialVariantIds.length,
    });
    if (!variantIds.length || !window.routes || !window.routes.cart_add_url) {
      track('Wishlist Added to Cart', Object.assign(baseProperties, {
        skipped_count: initialVariantIds.length,
        added_count: 0,
        result: 'failure',
        failure_stage: 'validation',
      }));
      return;
    }

    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const sections = cart && typeof cart.getSectionsToRender === 'function'
      ? cart.getSectionsToRender().map((section) => section.id)
      : ['cart-icon-bubble'];
    setCartButtonLoading(button, true);

    let skippedCount = 0;
    let cartAccepted = false;
    try {
      if (skipExisting && window.routes.cart_url) {
        const currentVariantIds = await fetchCartVariantIds();
        if (currentVariantIds) {
          cartVariantIds = currentVariantIds;
          variantIds = variantIds.filter((id) => !cartVariantIds.has(id));
          skippedCount = initialVariantIds.length - variantIds.length;
          syncWishlistCartButtons();
        }

        if (!variantIds.length) {
          showToast('모든 상품이 이미 장바구니에 있습니다.');
          track('Wishlist Added to Cart', Object.assign(baseProperties, {
            requested_count: 0,
            skipped_count: skippedCount,
            added_count: 0,
            result: 'success',
          }));
          return;
        }
      }

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id, quantity: 1 })),
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const cartData = await response.json();
      if (!response.ok || cartData.status) throw new Error(cartData.description || 'Cart add failed');

      const requestedItems = variantIds.map((id) => {
        const itemButton = Array.from(document.querySelectorAll('[data-fwd-wishlist-add-to-cart]'))
          .find((candidate) => variantId(candidate.dataset.variantId) === id);
        const catalogItem = Object.assign({}, findWishlistItem(id) || {}, itemButton ? buttonData(itemButton) : {});
        return Object.assign(
          {},
          itemProperties(catalogItem, 'wishlist'),
          { variant_id: id, quantity: 1 }
        );
      });
      const addedCount = trackAddedCartItems(cartData, requestedItems, requestId);

      track('Wishlist Added to Cart', Object.assign(baseProperties, {
        requested_count: variantIds.length,
        skipped_count: skippedCount,
        added_count: addedCount,
        result: 'success',
      }));
      cartAccepted = true;

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'fwd-wishlist',
          productVariantId: variantIds[0],
          cartData,
        });
      }

      if (cart && cartData.sections && typeof cart.renderContents === 'function') {
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        const renderData = cart.tagName === 'CART-NOTIFICATION' && cartData.items && cartData.items[0]
          ? Object.assign({}, cartData, cartData.items[0])
          : cartData;
        cart.renderContents(renderData);
      }

      variantIds.forEach((id) => cartVariantIds.add(id));

      showToast(
        variantIds.length === 1
          ? '상품을 장바구니에 추가했습니다.'
          : `${variantIds.length}개 상품을 장바구니에 추가했습니다.`
      );
    } catch (error) {
      if (cartAccepted) {
        showToast('상품을 장바구니에 추가했습니다.');
        return;
      }
      showToast('장바구니에 추가하지 못했습니다. 잠시 후 다시 시도해 주세요.');
      track('Wishlist Added to Cart', Object.assign(baseProperties, {
        requested_count: variantIds.length,
        skipped_count: skippedCount,
        added_count: 0,
        result: 'failure',
        failure_stage: 'cart_add',
      }));
    } finally {
      setCartButtonLoading(button, false);
      syncWishlistCartButtons();
    }
  }

  function addAllToCart(button) {
    const page = document.querySelector(PAGE_SELECTOR);
    if (!page) return;
    const variantIds = Array.from(
      page.querySelectorAll('[data-fwd-wishlist-add-to-cart]:not([disabled])')
    ).map((itemButton) => itemButton.dataset.variantId);
    addVariantsToCart(variantIds, button, true);
  }

  function clearWishlist() {
    const items = wishlistItems.slice();
    if (!items.length) return;
    wishlistItems = [];
    items.forEach((item) => queueWishlistOperation('removeFromWishList', item));
    persistWishlist();
    updateHeaderCount();
    syncButtons();
    renderWishlistPage();
    showToast('위시리스트를 모두 삭제했습니다.');
    flushPendingOperations();
    track('Wishlist Cleared', { wishlist_count: items.length, placement: 'wishlist' });
  }

  function bindEvents() {
    if (eventsBound) return;
    eventsBound = true;
    document.addEventListener('click', (event) => {
      const button = event.target.closest(BUTTON_SELECTOR);
      if (button) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        toggleWishlist(button);
        return;
      }
      const addToCartButton = event.target.closest('[data-fwd-wishlist-add-to-cart]');
      if (addToCartButton) {
        event.preventDefault();
        addVariantsToCart([addToCartButton.dataset.variantId], addToCartButton);
        return;
      }
      const addAllButton = event.target.closest('[data-fwd-wishlist-add-all]');
      if (addAllButton) {
        event.preventDefault();
        addAllToCart(addAllButton);
        return;
      }
      const clearButton = event.target.closest('[data-fwd-wishlist-clear]');
      if (clearButton) {
        const dialog = document.querySelector('[data-fwd-wishlist-dialog]');
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      }
    }, true);

    const dialog = document.querySelector('[data-fwd-wishlist-dialog]');
    if (dialog) {
      dialog.addEventListener('close', () => {
        if (dialog.returnValue === 'confirm') clearWishlist();
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const selectedVariantId = event && event.data && event.data.variant && event.data.variant.id;
        if (!selectedVariantId) return;
        document.querySelectorAll('.fwd-product-wishlist-button').forEach((button) => {
          button.dataset.variantId = String(selectedVariantId);
        });
        syncButtons();
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'fwd-wishlist') return;
        refreshWishlistCartState();
      });
    }

    if (typeof window.addEventListener === 'function') {
      window.addEventListener('online', () => {
        refreshWishlistFromRemote();
        flushPendingOperations();
      });
    }

    document.addEventListener('fwd:product-cards-rendered', () => {
      syncButtons();
    });
  }

  function bindSwymIdentityEvents() {
    if (swymEventsBound || !swymApi || !swymApi.evtLayer || typeof swymApi.evtLayer.addEventListener !== 'function') return;
    swymEventsBound = true;
    swymApi.evtLayer.addEventListener('sw:customerInfoRefreshed', () => {
      refreshWishlistFromRemote();
    });
  }

  function initialize(swat) {
    if (initialized) return true;
    const api = swat || window._swat;
    if (!api || typeof api.fetch !== 'function') return false;
    swymApi = api;
    initialized = true;
    window.clearTimeout(readyTimer);
    bindSwymIdentityEvents();
    refreshWishlistFromRemote();
    return true;
  }

  pendingOperations = readPendingOperations();
  const localItems = readLocalWishlist();
  if (localItems) wishlistItems = localItems;
  bindEvents();
  updateHeaderCount();
  syncButtons();
  renderWishlistPage();
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(initialize);
  if (!initialize(window._swat)) {
    readyTimer = window.setTimeout(() => {
      if (initialize(window._swat)) return;
      renderWishlistPage();
    }, SWYM_READY_TIMEOUT_MS);
  }
})();
